import React, { memo, useEffect, useState, useMemo } from 'react'
import { Button, Input, Modal, Row, Space, Table, Typography } from 'antd'
import Column from 'antd/lib/table/Column'
import {
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { ChemicalComponent } from '@/services/chemical-component'
import classnames from 'classnames'
import { AddEditComponent } from './AddEditComponent'
import { CreateWithJsonModal } from './CreateWithJsonModal'

interface ChemicalComponentListProps {
  orgId?: number
}

export const ChemicalComponentList = memo((props: ChemicalComponentListProps) => {
  const { orgId } = props
  
  const service = useMemo(() => {
    if (orgId) {
      return {
        get: (params) => ChemicalComponent.OrganizationService.get(orgId, params),
        getOne: (params) => ChemicalComponent.OrganizationService.getOne(orgId, params),
        create: (data) => ChemicalComponent.OrganizationService.create(orgId, data),
        update: (id, data) => ChemicalComponent.OrganizationService.update(orgId, id, data),
        remove: (id) => ChemicalComponent.OrganizationService.remove(orgId, id)
      }
    }
    return ChemicalComponent.AdminService
  }, [orgId])

  const { data: res, loading, refresh } = useRequest(() => service.get({}), {
    refreshDeps: [service]
  })
  const [data, setData] = useState<ChemicalComponent.Modal[]>(res as any)
  const [text, setText] = useState('')

  useEffect(() => {
    if (!res) return
    if (!text) {
      setData([...res])
      return
    }
    const search = text.toLowerCase()
    const tableData = res.filter(
      (item) => item.name?.toLowerCase().indexOf(search) > -1
        || item.formula?.toLowerCase().indexOf(search) > -1
        || item.cas?.toLowerCase().indexOf(search) > -1
    )
    setData(tableData)
  }, [res, text])

  return (
    <div>
      <Typography.Title level={5}>Chemical Components </Typography.Title>
      <Row justify={'space-between'}>
        <Input
          placeholder='Search by Name, Formula and CAS NO.'
          defaultValue={text}
          onChange={(e) => setText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Space>
          <Button type='primary' icon={<PlusOutlined />} onClick={() => {
            AddEditComponent.show({onSuccess: () => refresh(), orgId})
          }}>
            Create
          </Button>
          <Button icon={<PlusOutlined />} onClick={() => {
            CreateWithJsonModal.show({onSuccess: () => refresh(), orgId})
          }}>Create via Data</Button>
        </Space>
        
      </Row>

      <div className={classnames('mt-2')}>
        <Table
          bordered
          pagination={{ pageSize: 10 }}
          size='small'
          loading={loading}
          dataSource={data}
          rowKey={(record) => record.id}
        >
          <Column title='Name' dataIndex='name' key='name' align={'center'} />

          <Column title='Formula' dataIndex='formula' key='formula' align={'center'} />

          <Column
            title='Actions'
            align={'center'}
            render={(id, record: any, index) => {
              return (
                <Space>
                  <Button size='small' onClick={() => {
                    service.getOne({id: record.id}).then((componentDetail) => {
                      AddEditComponent.show({onSuccess: () => refresh(), component: componentDetail, orgId})
                    })
                  }}>Edit</Button>
                  <Button size='small' danger onClick={() => {
                    Modal.confirm({
                      icon: <ExclamationCircleOutlined />,
                      content: 'Are you sure to delete?',
                      onOk: async () => {
                        try {
                          await service.remove(record.id)
                          refresh()
                          return true
                        } catch (error) {
                          return false
                        }
                      }
                    })
                  }}>Delete</Button>
                </Space>
              )
            }}
          />
        </Table>
      </div>
    </div>
  )
})

export default ChemicalComponentList
